import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, Image, StyleSheet, TouchableHighlight, View } from 'react-native';
import PropTypes from 'prop-types';

import { locationsIDs, Theme } from '@constants';
import { useContextReference, useIndexedStateReference, useNodeHandle, useNodeHandleFromIndex } from '@hooks';

import { editIcons } from '../../../assets/icons/locations';
import { isAndroid, isWeb } from '../../../helpers/Platform';
import { FOCUS_NOW } from '../../../helpers/Tools';
import { FocusableHighlight } from '../../focusable/FocusableHighlight/FocusableHighlight';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'locations';

export const UserLocationsList = ({
  data,
  style,
  setVisiblePanel,
  addButtonReference,
  deleteLocation,
  setDefault,
  setWatching,
}) => {
  const [textButtonStateReferences, , setTextButtonStateReferences] = useIndexedStateReference({});
  const [homeButtonStateReferences, , setHomeButtonStateReferences] = useIndexedStateReference({});
  const [editButtonStateReferences, , setEditButtonStateReferences] = useIndexedStateReference({});
  const [deleteButtonStateReferences, , setDeleteButtonStateReferences] = useIndexedStateReference({});

  const listReference = useContextReference(locationsIDs.userLocationsList);

  const [focusedRowIndex, setFocusedRowIndex] = useState(null);
  const [focusedItemIndex, setFocusedItemIndex] = useState(0);
  const addButtonHandle = useNodeHandle(addButtonReference);

  const { t } = useTranslation('translation', { keyPrefix: tPath });

  const renderItem = ({ item, index }) =>
    data &&
    textButtonStateReferences &&
    homeButtonStateReferences &&
    editButtonStateReferences &&
    deleteButtonStateReferences ? (
      <View style={styles.locationRow}>
        {/* text button */}
        <ItemButton
          accessibilityHint={`${t(
            item.isWatching ? 'watchingLocationButtonAccessibilityHint' : 'changeLocationButtonAccessibilityHint',
          )} ${index === data.length - 1 ? '' : t('changeLocationButtonMoreHint')}`}
          accessibilityLabel={t(
            item.isWatching ? 'watchingLocationButtonAccessibilityLabel' : 'changeLocationButtonAccessibilityLabel',
            {
              location: item.name,
            },
          )}
          dataLength={data.length}
          deleteButtonReferences={deleteButtonStateReferences}
          editButtonReferences={editButtonStateReferences}
          homeButtonReferences={homeButtonStateReferences}
          index={index}
          item={item}
          listFocusUp={addButtonHandle}
          position={0}
          ref={itemReference => {
            setTextButtonStateReferences(itemReference, index);
          }}
          rowFocused
          rowItemFocused={focusedItemIndex === 0}
          style={styles.textButton}
          styleFocused={styles.textButtonFocused}
          testID={`change_city_modal_city_button_${index}`}
          textButtonReferences={textButtonStateReferences}
          onFocus={event => {
            setFocusedRowIndex(index);
            setFocusedItemIndex(0);
          }}
          onPress={() => {
            setWatching(index);
          }}
        >
          <ItemText item={item} rowFocused />
        </ItemButton>
        <View style={[styles.editButtons, focusedRowIndex === index ? styles.editButtonsShown : null]}>
          {/* home button */}
          {item.isDefault ? null : (
            <ItemButton
              accessibilityHint={t('setLocationAsHomeButtonAccessibilityHint')}
              accessibilityLabel={t('setLocationAsHomeButtonAccessibilityLabel', {
                location: item.name,
              })}
              buttonLabel={t('listPanel.setDefaultButtonLabel')}
              dataLength={data.length}
              deleteButtonReferences={deleteButtonStateReferences}
              editButtonReferences={editButtonStateReferences}
              homeButtonReferences={homeButtonStateReferences}
              index={index}
              item={item}
              listFocusUp={addButtonHandle}
              position={1}
              ref={itemReference => {
                setHomeButtonStateReferences(itemReference, index);
              }}
              rowFocused={focusedRowIndex === index}
              rowItemFocused={focusedItemIndex === 1}
              style={styles.iconButton}
              testID={`change_city_modal_make_home_button_${index}`}
              textButtonReferences={textButtonStateReferences}
              onFocus={() => {
                setFocusedRowIndex(index);
                setFocusedItemIndex(1);
              }}
              onPress={() => setDefault(index)}
            >
              <ItemIcon
                icon={editIcons.home}
                iconFocused={editIcons.homeFocused}
                rowFocused={focusedRowIndex === index}
              />
            </ItemButton>
          )}

          {/* edit button */}
          <ItemButton
            accessibilityHint={''}
            accessibilityLabel={t('editLocationAccessibilityLabel', {
              location: item.name,
            })}
            buttonLabel={t('listPanel.editButtonLabel')}
            dataLength={data.length}
            deleteButtonReferences={deleteButtonStateReferences}
            editButtonReferences={editButtonStateReferences}
            homeButtonReferences={homeButtonStateReferences}
            index={index}
            item={item}
            listFocusUp={addButtonHandle}
            position={2}
            ref={itemReference => {
              setEditButtonStateReferences(itemReference, index);
            }}
            rowFocused={focusedRowIndex === index}
            rowItemFocused={focusedItemIndex === 2}
            style={styles.iconButton}
            testID={`change_city_modal_make_home_button_${index}`}
            textButtonReferences={textButtonStateReferences}
            onFocus={() => {
              setFocusedRowIndex(index);
              setFocusedItemIndex(2);
            }}
            onPress={() => {
              setVisiblePanel({ key: 'edit', locationId: item.id });
            }}
          >
            <ItemIcon
              icon={editIcons.edit}
              iconFocused={editIcons.editFocused}
              rowFocused={focusedRowIndex === index}
            />
          </ItemButton>
          {/* delete button */}
          {item.isDefault ? null : (
            <ItemButton
              accessibilityHint={''}
              accessibilityLabel={t('deleteLocationButtonAccessibilityLabel', {
                location: item.name,
              })}
              buttonLabel={t('listPanel.deleteButtonLabel')}
              dataLength={data.length}
              deleteButtonReferences={deleteButtonStateReferences}
              editButtonReferences={editButtonStateReferences}
              homeButtonReferences={homeButtonStateReferences}
              index={index}
              item={item}
              listFocusUp={addButtonHandle}
              position={3}
              ref={itemReference => {
                setDeleteButtonStateReferences(itemReference, index);
              }}
              rowFocused={focusedRowIndex === index}
              rowItemFocused={focusedItemIndex === 3}
              style={styles.iconButton}
              testID={`change_city_modal_delete_city_button_${index}`}
              textButtonReferences={textButtonStateReferences}
              onFocus={() => {
                setFocusedRowIndex(index);
                setFocusedItemIndex(3);
              }}
              onPress={() => deleteLocation(index)}
            >
              <ItemIcon
                icon={editIcons.trash}
                iconFocused={editIcons.trashFocused}
                rowFocused={focusedRowIndex === index}
              />
            </ItemButton>
          )}
        </View>
      </View>
    ) : null;

  return (
    <>
      <FlatList
        data={data}
        keyExtractor={item => `${item.id}`}
        renderItem={renderItem}
        style={style}
        onBlur={() => {
          setFocusedRowIndex(null);
        }}
        onEndReached={() => {}}
      />
      <TouchableHighlight
        accessible
        nativeID={'user_location_list_focus_handle'}
        ref={listReference}
        style={styles.focusTarget}
        testID={'user_location_list_focus_handle'}
        onFocus={() => {
          FOCUS_NOW(textButtonStateReferences.current[Math.min(data.length - 1, focusedRowIndex)]);
        }}
      >
        <View />
      </TouchableHighlight>
    </>
  );
};

UserLocationsList.propTypes = {
  data: PropTypes.array.isRequired,
  setVisiblePanel: PropTypes.func.isRequired,
  addButtonReference: PropTypes.object.isRequired,
  deleteLocation: PropTypes.func.isRequired,
  setDefault: PropTypes.func.isRequired,
  setWatching: PropTypes.func.isRequired,
};

const ItemIcon = ({ icon, iconFocused, rowFocused, ...properties }) => {
  const focused = properties.focused;

  return (
    <>
      {rowFocused ? <View style={styles.buttonRowSelectBackground} /> : null}
      <Image
        source={focused ? iconFocused : icon}
        style={[styles.iconImage, focused ? styles.iconImageFocused : null]}
      />
    </>
  );
};

const ItemText = ({ item, rowFocused, ...properties }) => {
  const focused = properties.focused;
  let name = item.name;

  if (!name || name === '') {
    name = item.fullName;
  }

  const { t } = useTranslation('translation', { keyPrefix: tPath });

  return (
    <>
      {rowFocused ? <View style={styles.buttonRowSelectBackground} /> : null}
      <View style={styles.textButtonContents}>
        <TWCText
          importantForAccessibility={'no'}
          style={
            focused ? [styles.textButtonWatchingLabel, styles.locationItemTextFocused] : styles.textButtonWatchingLabel
          }
          testID={locationsIDs.userLocationsListWatchingText}
          variant={'regularText24'}
        >
          {item.isWatching ? t('listPanel.watchingLabel') : ' '}
        </TWCText>

        <View style={item.isDefault ? styles.locationTextWrapperDefault : styles.locationTextWrapper}>
          {item.isDefault ? (
            <Image
              source={focused ? editIcons.homeFocused : editIcons.home}
              style={[styles.homeFlagIcon, focused ? styles.homeFlagIconFocused : null]}
            />
          ) : null}
          <TWCText
            fontFamily={'light'}
            importantForAccessibility={'no'}
            numberOfLines={1}
            style={focused ? [styles.locationItemText, styles.locationItemTextFocused] : styles.locationItemText}
            testID={`location_text_${item.id}`}
          >
            {name}
          </TWCText>
        </View>
      </View>
    </>
  );
};

const ItemButton = forwardRef(
  (
    {
      accessibilityLabel,
      accessibilityHint,
      buttonLabel,
      item,
      index,
      listFocusUp,
      dataLength,
      textButtonReferences,
      homeButtonReferences,
      editButtonReferences,
      deleteButtonReferences,
      position,
      testID,
      style,
      styleFocused,
      children,
      onFocus,
      onBlur,
      rowFocused,
      rowItemFocused,
      onPress,
      ...properties
    },
    reference,
  ) => {
    const nextHandle = useNodeHandleFromIndex(textButtonReferences, index < dataLength - 1 ? index + 1 : null);

    const previousHandle = useNodeHandleFromIndex(textButtonReferences, index > 0 ? index - 1 : null);

    const textHandle = useNodeHandleFromIndex(textButtonReferences, index);
    const homeHandle = useNodeHandleFromIndex(homeButtonReferences, index);
    const editHandle = useNodeHandleFromIndex(editButtonReferences, index);
    const deleteHandle = useNodeHandleFromIndex(deleteButtonReferences, index);

    let nextFocusLeft = position === 1 ? textHandle : position === 2 ? homeHandle : position === 3 ? editHandle : null;

    let nextFocusRight =
      position === 0 ? homeHandle : position === 1 ? editHandle : position === 2 ? deleteHandle : null;

    // in the case of the home item, we skip the home button (it isn't rendered for this item)
    // currently not working, not sure why
    if (item.isDefault) {
      nextFocusLeft = position === 2 ? textHandle : position === 3 ? editHandle : null;

      nextFocusRight = position === 0 ? editHandle : null;
    }

    const { t } = useTranslation('translation', { keyPrefix: tPath });
    const sharedLabel = t('locationsListItemHint', {
      index: index + 1,
      length: dataLength,
    });

    const wrapperStyle = index === 0 ? { marginTop: 0 } : { marginTop: isAndroid ? Theme.px(-18) : Theme.px(-14) };

    return (
      <View style={[styles.buttonItemWrapper, wrapperStyle]}>
        <FocusableHighlight
          accessibilityHint={accessibilityHint}
          accessibilityLabel={`${sharedLabel}${accessibilityLabel}`}
          nextFocusDown={nextHandle}
          nextFocusLeft={nextFocusLeft}
          nextFocusRight={nextFocusRight}
          nextFocusUp={index === 0 ? listFocusUp : previousHandle}
          ref={reference}
          style={style}
          styleFocused={styleFocused}
          testID={testID}
          underlayColor={Theme.white}
          onBlur={onBlur}
          onFocus={onFocus}
          onPress={onPress}
        >
          {children}
        </FocusableHighlight>
        {rowItemFocused ? (
          <TWCText noTestID importantForAccessibility={'no'} style={styles.buttonItemLabel} variant={'regularText20'}>
            {buttonLabel ? buttonLabel : ' '}
          </TWCText>
        ) : (
          <View style={styles.buttonItemLabelSpacer} />
        )}
      </View>
    );
  },
);

const styles = StyleSheet.create({
  buttonItemLabel: {
    height: isAndroid ? Theme.px(30) : Theme.px(24),
    paddingTop: Theme.px(4),
    textAlign: 'center',
    width: Theme.px(112),
  },
  buttonItemLabelSpacer: {
    height: isAndroid ? Theme.px(30) : Theme.px(24),
  },
  buttonItemWrapper: {
    flexDirection: 'column',
  },
  buttonRowSelectBackground: {
    backgroundColor: Theme.white,
    borderRadius: Theme.tileBorderRadius,
    height: Theme.px(112),
    opacity: 0.15,
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  editButtons: {
    flex: 1,
    flexDirection: 'row',
    opacity: 0,
  },
  editButtonsShown: {
    opacity: 1,
  },
  focusTarget: {
    height: 1,
    opacity: 1,
    width: 1,
  },
  homeFlagIcon: {
    height: Theme.px(50),
    marginRight: Theme.px(16),
    marginTop: isWeb ? Theme.px(-3) : Theme.px(1),
    opacity: 0.4,
    resizeMode: 'contain',
    width: Theme.px(50),
  },
  homeFlagIconFocused: {
    opacity: 1,
  },
  iconButton: {
    alignItems: 'center',
    borderRadius: Theme.tileBorderRadius,
    height: Theme.px(112),
    justifyContent: 'center',
    marginRight: Theme.px(15),
    width: Theme.px(112),
  },
  iconButtonFocused: {
    backgroundColor: Theme.white,
  },
  iconImage: {
    height: Theme.px(50),
    margin: Theme.px(15),
    opacity: 0.4,
    resizeMode: 'contain',
    width: Theme.px(50),
  },
  iconImageFocused: {
    opacity: 1,
  },
  locationItemText: {
    color: Theme.white,
    fontSize: Theme.px(38),
    height: '100%',
  },
  locationItemTextFocused: {
    color: Theme.colorFocusText,
  },
  locationRow: {
    flex: 1,
    flexDirection: 'row',
  },
  locationTextWrapper: {
    flexDirection: 'row',
    marginTop: Theme.px(-5),
  },
  locationTextWrapperDefault: {
    flexDirection: 'row',
    marginTop: Theme.px(0),
  },
  textButton: {
    borderRadius: Theme.tileBorderRadius,
    height: Theme.px(112),
    marginRight: Theme.px(15),
    width: Theme.px(600),
  },
  textButtonContents: {
    flexDirection: 'column',
    marginLeft: isWeb ? Theme.px(25) : Theme.px(21),
    marginTop: isWeb ? Theme.px(7) : Theme.px(1),
  },
  textButtonFocused: {
    backgroundColor: Theme.white,
  },
  textButtonWatchingLabel: {
    color: Theme.withOpacity(Theme.mainText, 0.5),
    marginBottom: Theme.px(4),
  },
});
